<template>
  <div v-if="multiSitePayload.length > 0 && renderComponent">
    <div class="columns">
      <div class="column" :class="$style.headerTitleColumn">
        <label
          :class="[$style.tableName, $style.criteriaBasedLabel]"
          class="label"
          >CRITERIA BASED SAMPLING</label
        >
      </div>
    </div>
    <b-table :class="$style.criteriaBasedTable" :data="auditCriteriaList">
      <template slot-scope="props">
        <b-table-column field="index" label="No." width="10px">
          {{ props.index + 1 }}.
        </b-table-column>
        <b-table-column
          field="criteria"
          label="Data Collection Period Criteria"
          width="70%"
        >
          {{ props.row.criteriaTitle }}
        </b-table-column>
        <b-table-column
          width="20%"
          field="isSamplingEnabled"
          label="Enable Sampling"
        >
          <bField
            horizontal
            class="sampleSizeCheckboxContainer"
            :class="$style.allSitesLabel"
            label="All Sites"
          >
            <b-checkbox
              class="input-checkbox"
              :disabled="!editable"
              v-model="props.row.isSamplingEnabled"
              @change.native="updateChildSampling(props.index)"
            ></b-checkbox>
          </bField>
          <template v-for="(site, siteIndex) in siteArray">
            <bField
              horizontal
              class="sampleSizeCheckboxContainer"
              :label="site.name"
            >
              <b-checkbox
                class="input-checkbox"
                :disabled="!editable"
                v-model="props.row.sites[siteIndex].isSamplingEnabled"
                @change.native="updateParentSampling(props.index, siteIndex)"
              >
              </b-checkbox>
            </bField>
          </template>
        </b-table-column>
      </template>
    </b-table>
    <div :class="$style.saveChangesContainer" v-if="editable">
      <b-button
        @click="handleMultiSiteChanges"
        :class="$style.saveChangesBtn"
        type="is-primary"
        >Save</b-button
      >
    </div>
  </div>
</template>
<script lang="ts">
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import DefaultLayout from '@/layouts/DefaultLayout.vue';
import { Action, State } from 'vuex-class';
import HeroBar from '@/components/base/HeroBar.vue';
import { isDifferent, isTruthy } from '@/jbi-shared/util/watcher.vue-decorator';
import {
  AuditSamplingSite,
  MultiSitePayload,
  MultiSiteSamplingRequestPayload,
  AuditSite,
  AuditSamplingResponse,
  AuditSamplingDataConfig,
  AuditCriteriaMapStatus,
} from '@/store/modules/audits/types/audits.types';
import {
  map as _map,
  round as _round,
  cloneDeep as _cloneDeep,
  orderBy as _orderBy,
  findIndex as _findIndex,
  find as _find,
} from 'lodash';
import { ToastProgrammatic as Toast } from 'buefy';
import SamplingCalculator from '@/views/Audit/components/SamplingCalculator.vue';
import ConsecutiveCalculator from '@/views/Audit/components/ConsecutiveCalculator.vue';
import ConfirmationDialogue from '@/components/ConfirmationDialogue.vue';
import { AuditSamplingType } from '@/jbi-shared/types/project.types';

@Component({
  components: {
    ConsecutiveCalculator,
    SamplingCalculator,
    DefaultLayout,
    HeroBar,
  },
})
export default class AdHocMultiSiteSampling extends Vue {
  @Prop()
  public auditId!: number;
  @Prop()
  public auditSiteList!: AuditSite[];
  @Prop()
  public auditConfig!: AuditSamplingResponse;
  @Prop()
  public auditSamplingType!: AuditSamplingType;
  @Prop()
  public auditDataCollectionDate!: string;
  @Prop({ default: true })
  public editable!: boolean;
  public multiSitePayload: MultiSitePayload[] = [];
  public siteArray: AuditSamplingSite[] = [];
  public renderComponent: boolean = true;
  public criteriaSiteIndex: any = [];

  @Action('audits/updateMultiSiteSampling')
  public updateMultiSiteSampling!: (
    payload: MultiSiteSamplingRequestPayload,
  ) => void;

  @State((state) => state.audits.updatedMultiSiteSampling)
  public updatedMultiSiteSampling!: any;

  @Watch('updatedMultiSiteSampling')
  @isTruthy
  public watchUpdatedMultiSiteSampling() {
    this.showNotificationAndRedirectToProject();
  }

  public mounted() {
    this.populateMultiSiteData();
  }

  public populateMultiSiteData() {
    this.auditSiteList.map((auditSite: AuditSite) => {
      const siteObject: AuditSamplingSite = {
        siteId: auditSite.site.id,
        name: auditSite.site.name,
        isSamplingEnabled: false,
        auditSiteId: auditSite.id,
      };
      this.siteArray.push(siteObject);
    });

    this.auditConfig.dataConfigs.forEach(
      (dataConfig: AuditSamplingDataConfig) => {
        const currentCriteriaPayload: MultiSitePayload = {
          auditId: this.auditId,
          criteriaId: 0, // todo: change this logic later
          auditCriteriaId: 0,
          sites: [],
          sampleSize: 0,
        };
        Object.values(dataConfig).forEach(
          (singleConfig: AuditSamplingDataConfig) => {
            if (
              singleConfig.auditCriteriaMap.status ===
              AuditCriteriaMapStatus.ACTIVE
            ) {
              currentCriteriaPayload.criteriaId =
                singleConfig.auditCriteriaMap.criteria.id;
              currentCriteriaPayload.auditId = this.auditId;
              currentCriteriaPayload.auditCriteriaId =
                singleConfig.auditCriteriaMap.id;
              currentCriteriaPayload.criteriaTitle =
                singleConfig.auditCriteriaMap.criteria.title;
              currentCriteriaPayload.order =
                singleConfig.auditCriteriaMap.order;
              if (singleConfig.auditSiteMap === null) {
                currentCriteriaPayload.isSamplingEnabled =
                  singleConfig.isSamplingEnabled;
                currentCriteriaPayload.dataConfig = singleConfig;
              } else {
                const auditSamplingConfig = this.siteArray.find(
                  (value: AuditSamplingSite) => {
                    return value.auditSiteId === singleConfig.auditSiteMap.id;
                  },
                );
                if (auditSamplingConfig) {
                  currentCriteriaPayload.sites.push({
                    ...auditSamplingConfig,
                    isSamplingEnabled: singleConfig.isSamplingEnabled,
                    dataConfig: singleConfig,
                  });
                  currentCriteriaPayload.sites.sort((itemA, itemB) => {
                    return itemA.siteId - itemB.siteId;
                  });
                  const filteredCriteria: any = [];
                  currentCriteriaPayload.sites.map((data) =>
                    filteredCriteria.filter(
                      (item: any) => item.siteId === data.siteId,
                    ).length > 0
                      ? null
                      : filteredCriteria.push(data),
                  );
                  currentCriteriaPayload.sites = filteredCriteria;
                }
              }
            }
          },
        );
        this.multiSitePayload.push(currentCriteriaPayload);
      },
    );
    this.multiSitePayload = this.multiSitePayload.map(
      (criteria: MultiSitePayload) => {
        const sitesIdsToCheckAgainst: number[] = this.siteArray.map(
          (site: AuditSamplingSite) => site.siteId,
        );
        return {
          ...criteria,
          sites: criteria.sites.sort(
            (optionA: AuditSamplingSite, optionB: AuditSamplingSite) => {
              if (
                sitesIdsToCheckAgainst.indexOf(optionA.siteId) >
                sitesIdsToCheckAgainst.indexOf(optionB.siteId)
              ) {
                return 1;
              } else {
                return -1;
              }
            },
          ),
        };
      },
    );
  }

  public showNotificationAndRedirectToProject() {
    Toast.open({
      message: `Changes saved`,
      position: 'is-top',
      type: 'is-dark',
      duration: 3500,
    });
  }

  public handleMultiSiteChanges() {
    this.updateMultiSiteSampling({
      auditId: this.auditId,
      multiSitePayload: this.multiSitePayload,
      auditSamplingType: this.auditSamplingType,
      samplingConfigId: this.auditConfig.auditSamplingConfigs.id,
    });
  }

  public updateChildSampling(index: number) {
    if (this.auditCriteriaList[index].isSamplingEnabled) {
      this.auditCriteriaList[index].sites.forEach((siteData) => {
        siteData.isSamplingEnabled = true;
      });
    } else {
      if (
        this.auditDataCollectionDate !== undefined &&
        this.auditDataCollectionDate !== null
      ) {
        this.$buefy.modal.open({
          parent: this,
          component: ConfirmationDialogue,
          hasModalCard: true,
          trapFocus: true,
          onCancel: () => {
            this.auditCriteriaList[index].isSamplingEnabled = true;
            this.auditCriteriaList[index].sites.forEach((siteData) => {
              siteData.isSamplingEnabled = true;
            });
          },
          props: {
            modalTitle: 'Disable Sampling?',
            confirmationText: `Disable Sampling will cease data collection
                            and delete collected data for this data collection period criteria.`,
            confirmationButtonText: 'Disable',
            cancelButtonText: 'Cancel',
          },
          events: {
            confirm: () => {
              this.auditCriteriaList[index].isSamplingEnabled = false;
              this.auditCriteriaList[index].sites.forEach((siteData) => {
                siteData.isSamplingEnabled = false;
              });
            },
            cancel: () => {
              this.auditCriteriaList[index].isSamplingEnabled = true;
              this.auditCriteriaList[index].sites.forEach((siteData) => {
                siteData.isSamplingEnabled = true;
              });
            },
          },
        });
      } else {
        this.auditCriteriaList[index].sites.forEach((siteData) => {
          siteData.isSamplingEnabled = false;
        });
      }
    }
  }

  public updateParentSampling(index: number, siteIndex: number) {
    let parentVal: boolean = true;
    if (
      this.auditDataCollectionDate !== undefined &&
      this.auditDataCollectionDate !== null &&
      !this.auditCriteriaList[index].sites[siteIndex].isSamplingEnabled
    ) {
      this.$buefy.modal.open({
        parent: this,
        component: ConfirmationDialogue,
        hasModalCard: true,
        trapFocus: true,
        onCancel: () => {
          this.auditCriteriaList[index].sites[
            siteIndex
          ].isSamplingEnabled = true;
          this.auditCriteriaList[index].sites.forEach((siteData) => {
            if (siteData.isSamplingEnabled === false) {
              parentVal = false;
            }
          });

          this.auditCriteriaList[index].isSamplingEnabled = parentVal;
        },
        props: {
          modalTitle: 'Disable Sampling?',
          confirmationText: `Disable Sampling will cease data collection
                            and delete collected data for this data collection period criteria.`,
          confirmationButtonText: 'Disable',
          cancelButtonText: 'Cancel',
        },
        events: {
          confirm: () => {
            this.auditCriteriaList[index].sites[
              siteIndex
            ].isSamplingEnabled = false;
            this.auditCriteriaList[index].sites.forEach((siteData) => {
              if (siteData.isSamplingEnabled === false) {
                parentVal = false;
              }
            });

            this.auditCriteriaList[index].isSamplingEnabled = parentVal;
          },
          cancel: () => {
            this.auditCriteriaList[index].sites[
              siteIndex
            ].isSamplingEnabled = true;
            this.auditCriteriaList[index].sites.forEach((siteData) => {
              if (siteData.isSamplingEnabled === false) {
                parentVal = false;
              }
            });

            this.auditCriteriaList[index].isSamplingEnabled = parentVal;
          },
        },
      });
    } else {
      this.auditCriteriaList[index].sites.forEach((siteData) => {
        if (siteData.isSamplingEnabled === false) {
          parentVal = false;
        }
      });

      this.auditCriteriaList[index].isSamplingEnabled = parentVal;
    }
  }

  get auditCriteriaList() {
    return _orderBy(this.multiSitePayload, ['order'], ['asc']);
  }
}
</script>
<style lang="scss" module>
.tableName {
  color: #444f51;
  font-weight: bold;
}
.saveChangesBtn {
  float: right;
  border-radius: 24px;
  height: 48px;
  width: 120px;
  padding: 0 calc(1em + 0.25em);
}
.paddingTop {
  padding-top: 30px;
}
.criteriaBasedLabel {
  margin-top: 12px;
  padding-top: 24px;
}
.saveChangesContainer {
  padding-top: 60px;
  text-align: right;
}
.calculatorHeader {
  float: right;
}
.headerTitleColumn {
  padding: 0 0.75rem;
}
.criteriaBasedTable > div > table {
  thead {
    background-color: #ffffff;
    tr {
      font-size: 14px;
      th {
        color: #9f9f9f;
        font-weight: initial !important;
        padding: 20px 0px 23px 12px;
      }
      th:first-child {
        padding-left: 20px;
      }
      th:nth-child(2) {
        padding-left: 42px;
      }
      th:last-child {
        padding-left: 0;
        padding-right: 20px;
        div {
          float: right;
        }
      }
    }
  }
  tbody {
    background-color: #ffffff;
    tr {
      td {
        color: #444f51;
        border: 0.5px solid #dbdbdb;
        border-width: 0 0 0.5px;
        padding-top: 20px;
        padding-bottom: 18px;
        padding-right: 0px;
      }
      td:first-child {
        padding-left: 20px;
      }
      td:last-child {
        padding-left: 0;
        padding-right: 20px;
        padding-top: 20px;
      }
      td:nth-child(2) {
        padding-left: 42px;
      }
      td:nth-child(3) {
        width: 100%;
        text-align: right;
      }
    }
    tr:last-child {
      td {
        padding-bottom: 14px;
      }
    }
  }
}
.allSitesLabel {
  div {
    label {
      font-weight: 700;
      color: #444f51;
    }
  }
}
</style>
